import { useState, useEffect } from 'react';
import { FirebaseService } from '../services/FirebaseService';
import { useAuth } from '../components/Auth/AuthContext';

// Create a simple event emitter for user profile updates
class UserProfileEventEmitter {
  private listeners: Array<(profile: { profileImageUrl?: string; displayName?: string }) => void> = [];

  subscribe(callback: (profile: { profileImageUrl?: string; displayName?: string }) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  emit(profile: { profileImageUrl?: string; displayName?: string }) {
    this.listeners.forEach(callback => callback(profile));
  }
}

export const userProfileEmitter = new UserProfileEventEmitter();

// Legacy export for backwards compatibility
export const profileImageEmitter = {
  subscribe: (callback: (imageUrl: string) => void) => {
    return userProfileEmitter.subscribe((profile) => {
      if (profile.profileImageUrl) {
        callback(profile.profileImageUrl);
      }
    });
  },
  emit: (imageUrl: string) => {
    userProfileEmitter.emit({ profileImageUrl: imageUrl });
  }
};

export const useProfileImage = () => {
  const { user } = useAuth();
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Initial fetch
  useEffect(() => {
    const fetchProfileImage = async () => {
      if (user?.uid) {
        try {
          const userProfile = await FirebaseService.fetchUserProfile();
          if (userProfile?.profileImageUrl) {
            setProfileImageUrl(userProfile.profileImageUrl);
          }
        } catch (error) {
          console.error('Error fetching profile image:', error);
        }
      }
      setIsLoading(false);
    };

    fetchProfileImage();
  }, [user]);

  // Listen for profile image updates
  useEffect(() => {
    const unsubscribe = userProfileEmitter.subscribe((profile) => {
      if (profile.profileImageUrl) {
        console.log('🔄 Profile image updated via emitter:', profile.profileImageUrl);
        setProfileImageUrl(profile.profileImageUrl);
      }
    });

    return unsubscribe;
  }, []);

  const updateProfileImage = (newImageUrl: string) => {
    setProfileImageUrl(newImageUrl);
    userProfileEmitter.emit({ profileImageUrl: newImageUrl });
  };

  return {
    profileImageUrl,
    isLoading,
    updateProfileImage
  };
};

export const useUserProfile = () => {
  const { user } = useAuth();
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Initial fetch
  useEffect(() => {
    const fetchProfile = async () => {
      if (user?.uid) {
        try {
          const userProfile = await FirebaseService.fetchUserProfile();
          if (userProfile?.profileImageUrl) {
            setProfileImageUrl(userProfile.profileImageUrl);
          }
          // Use displayName from auth (most up to date) or fallback to Firestore
          setDisplayName(user?.displayName || userProfile?.displayName || '');
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setDisplayName(user?.displayName || '');
      }
      setIsLoading(false);
    };

    fetchProfile();
  }, [user]);

  // Listen for profile updates
  useEffect(() => {
    const unsubscribe = userProfileEmitter.subscribe((profile) => {
      if (profile.profileImageUrl) {
        console.log('🔄 Profile image updated via emitter:', profile.profileImageUrl);
        setProfileImageUrl(profile.profileImageUrl);
      }
      if (profile.displayName) {
        console.log('🔄 Display name updated via emitter:', profile.displayName);
        setDisplayName(profile.displayName);
      }
    });

    return unsubscribe;
  }, []);

  const updateProfile = (updates: { profileImageUrl?: string; displayName?: string }) => {
    if (updates.profileImageUrl) {
      setProfileImageUrl(updates.profileImageUrl);
    }
    if (updates.displayName) {
      setDisplayName(updates.displayName);
    }
    userProfileEmitter.emit(updates);
  };

  return {
    profileImageUrl,
    displayName,
    isLoading,
    updateProfile
  };
};
